import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, addDoc, deleteDoc, doc, query, orderBy, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';

interface Purchase {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  productId: string;
  productName: string;
  productType: string;
  price: number;
  currency: string;
  licenseKey: string;
  purchaseDate: Date;
  expiryDate?: Date;
  status: 'active' | 'expired' | 'revoked';
  downloadCount: number;
  lastDownload?: Date;
}

const OwnershipTracking: React.FC = () => {
  const { t } = useTranslation();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    loadPurchases();
  }, []);

  const loadPurchases = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'marketplace_purchases'), orderBy('purchaseDate', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        purchaseDate: doc.data().purchaseDate?.toDate(),
        expiryDate: doc.data().expiryDate?.toDate(),
        lastDownload: doc.data().lastDownload?.toDate()
      })) as Purchase[];
      
      setPurchases(data);
    } catch (error) {
      console.error('Error loading purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (window.confirm(t('marketplace.confirmRevokeLicense'))) {
      try {
        const purchaseRef = doc(db, 'marketplace_purchases', id);
        await addDoc(collection(db, 'marketplace_purchases'), {
          status: 'revoked',
          revokedAt: Timestamp.now()
        });
        loadPurchases();
      } catch (error) {
        console.error('Error revoking license:', error);
      }
    }
  };

  const getStatusColor = (status: Purchase['status']) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'expired': return 'bg-yellow-500/20 text-yellow-400';
      case 'revoked': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const filteredPurchases = purchases.filter(p => {
    const matchesSearch = p.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.productName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('marketplace.searchPurchases')}
          className="input-dark flex-1"
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="input-dark w-48"
        >
          <option value="all">{t('common.all')}</option>
          <option value="active">{t('marketplace.status.active')}</option>
          <option value="expired">{t('marketplace.status.expired')}</option>
          <option value="revoked">{t('marketplace.status.revoked')}</option>
        </select>
      </div>

      <div className="card-dark overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#15191E]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('marketplace.user')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('marketplace.product')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('marketplace.price')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('marketplace.purchaseDate')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                Státusz
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('marketplace.downloads')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('common.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#31383D]">
            {filteredPurchases.map(purchase => (
              <tr key={purchase.id} className="hover:bg-[#15191E] transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-white">{purchase.userName}</div>
                    <div className="text-xs text-[#B8B8C3]">{purchase.userEmail}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-white">{purchase.productName}</div>
                    <div className="text-xs text-[#B8B8C3]">{purchase.productType}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-cyan-400 font-semibold">
                    {purchase.currency === 'USD' && '$'}
                    {purchase.currency === 'EUR' && '€'}
                    {purchase.currency === 'HUF' && 'Ft '}
                    {purchase.price.toLocaleString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-white">
                    {purchase.purchaseDate.toLocaleDateString()}
                  </div>
                  <div className="text-xs text-[#B8B8C3]">
                    {purchase.purchaseDate.toLocaleTimeString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(purchase.status)}`}>
                    {t(`marketplace.status.${purchase.status}`)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-white">{purchase.downloadCount}x</div>
                  {purchase.lastDownload && (
                    <div className="text-xs text-[#B8B8C3]">
                      {purchase.lastDownload.toLocaleDateString()}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {purchase.status === 'active' && (
                    <button
                      onClick={() => handleRevoke(purchase.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      {t('marketplace.revoke')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredPurchases.length === 0 && (
        <div className="card-dark p-12 text-center">
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-xl font-bold text-white mb-2">{t('marketplace.noPurchases')}</h3>
          <p className="text-[#B8B8C3]">{t('marketplace.noPurchasesDesc')}</p>
        </div>
      )}

      <div className="card-dark p-6">
        <h3 className="text-xl font-bold text-white mb-4">{t('marketplace.purchaseStats')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-cyan-400">
              {purchases.length}
            </div>
            <div className="text-sm text-[#B8B8C3]">{t('marketplace.totalPurchases')}</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-400">
              {purchases.filter(p => p.status === 'active').length}
            </div>
            <div className="text-sm text-[#B8B8C3]">{t('marketplace.activeLicenses')}</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-400">
              {purchases.filter(p => p.status === 'expired').length}
            </div>
            <div className="text-sm text-[#B8B8C3]">{t('marketplace.expiredLicenses')}</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-red-400">
              {purchases.filter(p => p.status === 'revoked').length}
            </div>
            <div className="text-sm text-[#B8B8C3]">{t('marketplace.revokedLicenses')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnershipTracking;
